import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye } from "lucide-react";

import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { showError } from "../../utils/toast.ts";
import { capitalizeWords } from "../../utils/format-display-text.ts";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { EmptyStateMessage } from "../../components/shared/empty-state-message/empty-state-message.tsx";
import { ProbationReviewToolbar } from "../../components/features/probation-review/components/probation-review-toolbar.tsx";
import { ProbationReviewModal } from "../../components/features/probation-review/modals/probation-review-modal.tsx";
import { useProbationCycles } from "../../components/features/probation-review/hooks/use-probation-cycles.ts";
import type { ProbationCycleRow } from "../../components/features/probation-review/schemas/probation-cycle-list.schema.ts";
import { probationStatusBadgeVariant } from "../../components/features/probation-review/utils/probation-status-badge.ts";
import { useEmployeesList } from "../../hooks/time-allocated-overview/use-employees-list.ts";
import { useReportingManagers } from "../../hooks/use-reporting-managers.ts";
import { useManagerStatus } from "../../hooks/use-manager-status.ts";
import { useUser } from "../../lib/rbac/useUser.ts";
import {
  ResponsiveDataTable,
  Badge,
  Spinner,
  type ResponsiveColumn,
  type SelectOption,
} from "../../components/ui/index.ts";
import type { PageProps } from "../../types/page.ts";

import styles from "./probation-review-page.module.css";

const STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: "", label: "All" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "REJECTED", label: "Rejected" },
  { value: "APPROVED", label: "Approved" },
  { value: "PENDING", label: "Pending" },
  { value: "EXTENDED", label: "Extend" },
];

type ProbationReviewTableRow = ProbationCycleRow & { id: string } & Record<string, unknown>;

function formatStatusLabel(status: string | undefined): string {
  if (status == null || status === "") return "—";
  return capitalizeWords(status.replace(/_/g, " "));
}

export default function ProbationReviewPage({ title }: PageProps) {
  const user = useUser();
  const managerStatus = useManagerStatus();
  const hasRoleAccess = Boolean(
    user?.roles?.includes("HR") || user?.roles?.includes("SUPERADMIN"),
  );
  const { setBreadcrumbs } = useBreadcrumbs();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState("");
  const [employeeIdFilter, setEmployeeIdFilter] = useState("");
  const [reportingManagerIdFilter, setReportingManagerIdFilter] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    status: "", employeeId: "", managerId: "",
  });
  const reportingManagers = useReportingManagers();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState<ProbationCycleRow | null>(null);

  const {
    options: employeeOptions,
    loading: employeesLoading,
    error: employeesError,
  } = useEmployeesList();

  const { rows, totalElements, loading, error, refetch } = useProbationCycles({
    page,
    pageSize,
    status: appliedFilters.status || undefined,
    employeeId: appliedFilters.employeeId || undefined,
    managerId: appliedFilters.managerId || undefined,
  });
  const isAssignedManager = (cycle: ProbationCycleRow | null) =>
    user?.id != null &&
    cycle?.managerId != null &&
    String(user.id) === String(cycle.managerId);
  const canAccessPage =
    hasRoleAccess || managerStatus.isReportingManager || rows.some(isAssignedManager);

  useEffect(() => {
    setBreadcrumbs([{ label: title ?? "Probation Review" }]);
    return () => setBreadcrumbs([]);
  }, [setBreadcrumbs, title]);

  useEffect(() => {
    if (error) showError(error);
  }, [error]);

  useEffect(() => {
    if (employeesError) showError(employeesError);
  }, [employeesError]);

  useEffect(() => {
    if (reportingManagers.error) showError(reportingManagers.error);
  }, [reportingManagers.error]);

  /** Keep modal cycle in sync after submit/refetch (dual HR+Manager role → HR form). */
  useEffect(() => {
    if (!modalOpen || selectedCycle == null) return;
    const fresh = rows.find(
      (row) => String(row.cycleId) === String(selectedCycle.cycleId),
    );
    if (fresh == null) return;
    if (
      fresh.managerSubmitted !== selectedCycle.managerSubmitted ||
      fresh.hrSubmitted !== selectedCycle.hrSubmitted ||
      fresh.status !== selectedCycle.status
    ) {
      setSelectedCycle(fresh);
    }
  }, [rows, modalOpen, selectedCycle]);

  const tableRows: ProbationReviewTableRow[] = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        id: String(row.cycleId),
      })),
    [rows],
  );

  const openCycle = useCallback((row: ProbationCycleRow) => {
    setSelectedCycle(row);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedCycle(null);
  }, []);

  const handlePageSizeChange = useCallback((next: number) => {
    setPageSize(next);
    setPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((status: string) => {
    setStatusFilter(status);
  }, []);

  const handleEmployeeIdFilterChange = useCallback((employeeId: string) => {
    setEmployeeIdFilter(employeeId);
  }, []);

  const handleApplyFilters = () => {
    setAppliedFilters({
      status: statusFilter,
      employeeId: employeeIdFilter,
      managerId: reportingManagerIdFilter,
    });
    setPage(1);
  };

  const handleClearFilters = () => {
    setStatusFilter("");
    setEmployeeIdFilter("");
    setReportingManagerIdFilter("");
    setAppliedFilters({ status: "", employeeId: "", managerId: "" });
    setPage(1);
  };

  const columns = useMemo<ResponsiveColumn<ProbationReviewTableRow>[]>(
    () => [
      {
        id: "employeeName",
        header: "Employee Name",
        accessor: "employeeName",
        sortable: true,
        truncate: true,
        render: (row) => (
          <span className="body-sm">{row.employeeName ?? "—"}</span>
        ),
        getSortValue: (row) => row.employeeName ?? "",
        getTitleText: (row) => row.employeeName ?? "",
      },
      {
        id: "designation",
        header: "Designation",
        accessor: "designation",
        sortable: true,
        truncate: true,
      },
      {
        id: "managerName",
        header: "Reporting Manager",
        accessor: "managerName",
        sortable: true,
        truncate: true,
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        nowrap: true,
        render: (row) => (
          <Badge variant={probationStatusBadgeVariant(row.status)}>
            {formatStatusLabel(row.status)}
          </Badge>
        ),
        getSortValue: (row) => row.status ?? "",
        getTitleText: (row) => formatStatusLabel(row.status),
      },
      {
        id: "actions",
        header: "View",
        sortable: false,
        nowrap: true,
        render: (row) => (
          <button
            type="button"
            className={styles.iconBtn}
            aria-label={`View probation review for ${row.employeeName ?? "employee"}`}
            onClick={() => openCycle(row)}
          >
            <Eye size={18} strokeWidth={2} aria-hidden />
          </button>
        ),
      },
    ],
    [openCycle],
  );

  return (
    <PageShell>
      {managerStatus.loading && !managerStatus.verified && !canAccessPage ? (
        <Spinner />
      ) : canAccessPage ? (
        <>
        <ProbationReviewToolbar
          statusOptions={STATUS_FILTER_OPTIONS}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          employeeIdFilter={employeeIdFilter}
          onEmployeeIdFilterChange={handleEmployeeIdFilterChange}
          employeeOptions={employeeOptions}
          employeesLoading={employeesLoading}
          managerId={reportingManagerIdFilter}
          onReportingManagerChange={setReportingManagerIdFilter}
          reportingManagerOptions={reportingManagers.options}
          reportingManagersLoading={reportingManagers.loading}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          loading={loading}
        />

        {loading ? (
          <Spinner />
        ) : (
          <div className={styles.tableSection}>
            <ResponsiveDataTable<ProbationReviewTableRow>
              serialNumber
              columns={columns}
              data={tableRows}
              defaultSortKey="employeeName"
              defaultSortDir="asc"
              aria-label="Probation review cycles"
              emptyState={<EmptyStateMessage message="No probation cycles found." />}
              pagination
              page={page}
              pageSize={pageSize}
              totalItems={totalElements}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[5, 10, 20, 30, 40]}
              totalLabel={
                <>
                  Total Cycles: <strong>{totalElements}</strong>
                </>
              }
            />
          </div>
        )}

        <ProbationReviewModal
          open={modalOpen}
          onClose={closeModal}
          cycle={selectedCycle}
          isReportingManager={isAssignedManager(selectedCycle)}
          onSuccess={() => void refetch()}
        />
        </>
      ) : (
        <EmptyStateMessage message={managerStatus.error ?? "You do not have permission to access probation review."} />
      )}
    </PageShell>
  );
}
